import React, { useState, useEffect } from 'react';
import { Box, Button, Container, Grid, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CreateSiteForm from '../components/CreateSiteForm';
import SiteContent from '../components/SiteContent';
import SmartphoneMockup from '../components/SmartphoneMockup';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import { isValidEmail } from '../utils/helpers';
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router-dom";
import { apiService } from '../services/apiService';

const STORAGE_KEY = 'createSiteFormData';

const Create = () => {
  const location = useLocation();
  const { planId } = location.state || { planId: 'basic' };

  const { t, i18n } = useTranslation();

  const [isSaving, setIsSaving] = useState(false);
  const [formValues, setFormValues] = useState({
    planId,
    email: '',
    coupleName: '',
    startDateTime: '',
    message: '',
    youTubeUrl: '',
    imageUrls: []
  });

  const [showPaymentErrorModal, setShowPaymentErrorModal] = useState(false);

  useEffect(() => {
    const savedData = sessionStorage.getItem(STORAGE_KEY);
    if (savedData) {
      setFormValues(JSON.parse(savedData));
    }

    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get('canceled') === 'true') {
      setShowPaymentErrorModal(true);
    }
  }, []);

  const isCreateButtonDisabled = !(
    formValues.email?.trim() &&
    formValues.coupleName?.trim() &&
    formValues.startDateTime?.trim() &&
    formValues.message?.trim() &&
    formValues.imageUrls?.length > 0
  );

  const saveUserData = async () => {
    if (!isValidEmail(formValues.email)) {
      toast.error(t('error_invalid_email'));
      return;
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formValues));
    setIsSaving(true);

    // Add the current language to the data that is
    // going to be saved
    formValues.language = i18n.language;

    try {
      // Facebook Pixel - Initiate Checkout
      window.fbq('track', 'InitiateCheckout');

      const user = await apiService.createUser(formValues)
      const response = await apiService.checkout({ userId: user.id, planId: formValues.planId, email: formValues.email });

      window.location = response.redirectUrl;
    } catch (error) {
      toast.error(t('error_payment_url'));
      setIsSaving(false);
    }
  };

  return (
    <>
      <Header />
      <Container>
        <Grid container display='flex' justifyContent='center' padding={0}>
          <Grid item xs={12} sm={12} md={8} lg={8} sx={{ px: 2, py: 4 }}>
            <Typography color='primary' variant="h1" sx={{ fontSize: { xs: '2.125rem', sm: '3rem' }, fontWeight: 'bold', mb: 0 }}>
              {t('create_page_headline')}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 6 }}>
              {t('create_page_subheadline')}
            </Typography>
            <CreateSiteForm initialValues={formValues} onFormChange={(fv) => setFormValues(fv)} />
          </Grid>
          <Grid item xs={12} sm={8} md={4} lg={4} sx={{ px: { xs: 2, sm: 3 }, py: 4 }}>
            <Typography color='primary' variant="subtitle1" textAlign='center' sx={{ fontWeight: 'bold', mb: 2 }}>
              {t('create_page_mockup_header')}
            </Typography>
            <SmartphoneMockup width="100%" borderColor="#2e2e2e">
              <Box sx={{
                maxHeight: 600,
                minWidth: '100%',
                overflowY: 'scroll',
                pb: 4,
                pt: 8,
                position: 'relative'
              }}>
                <SiteContent
                  planId={formValues.planId}
                  imageUrls={formValues.imageUrls}
                  message={formValues.message}
                  startDateTime={formValues.startDateTime}
                  videoAutoplay={false}
                  videoUrl={formValues.youTubeUrl}
                />
              </Box>
            </SmartphoneMockup>
            <Button
              color="primary"
              disabled={isCreateButtonDisabled || isSaving}
              fullWidth
              onClick={saveUserData}
              variant="contained"
              sx={{
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: 4,
                position: 'relative'
              }}
            >
              {isSaving ? (
                <Box sx={{ p: 0 }}>
                  <CircularProgress color='white' size={24} />
                </Box>
              ) : (
                <>
                  <span>{t('create_page_cta')}</span>
                  {isCreateButtonDisabled && (
                    <Typography color="#fff" variant="caption" sx={{ mt: 0, lineHeight: '1rem' }}>
                      {t('create_page_cta_missing_data')}
                    </Typography>
                  )}
                </>
              )}
            </Button>
          </Grid>
        </Grid >
      </Container>
      <Footer />

      <Dialog open={showPaymentErrorModal} onClose={() => setShowPaymentErrorModal(false)}>
        <DialogTitle>{t('payment_not_successful_title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('payment_not_successful_text')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant='contained'
            onClick={() => setShowPaymentErrorModal(false)}
            color="primary"
            sx={{ height: 40, width: 50, fontSize: '1rem' }}
          >
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Create;
